import React, { useState } from 'react';
import { X, Star, MessageSquarePlus, User, Briefcase, CheckCircle2, Loader2, Info } from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../common/Toast';
import { useLanguage } from '../../context/LanguageContext';
import { localizeLawyerPosition } from '../../utils/helpers';

export default function ReviewSubmissionModal({ isOpen, onClose, lawyers = [], initialLawyerId = '' }) {
  const { t, currentLang } = useLanguage();

  const [lawyerList, setLawyerList] = useState(lawyers);
  const [selectedLawyerId, setSelectedLawyerId] = useState(initialLawyerId ? String(initialLawyerId) : '');
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [clientName, setClientName] = useState('');
  const [clientTitle, setClientTitle] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  React.useEffect(() => {
    if (initialLawyerId) {
      setSelectedLawyerId(String(initialLawyerId));
    }
  }, [initialLawyerId, isOpen]);

  React.useEffect(() => {
    if (lawyers && lawyers.length > 0) {
      setLawyerList(lawyers);
    } else if (isOpen) {
      api.get('/lawyers')
        .then((res) => {
          const list = res?.lawyers || res?.data?.lawyers || res?.data || (Array.isArray(res) ? res : []);
          setLawyerList(list);
        })
        .catch(() => {});
    }
  }, [lawyers, isOpen]);

  if (!isOpen) return null;

  const ratingLabels = {
    1: t('rating_1', '1 - Juda qoniqarsiz'),
    2: t('rating_2', '2 - Qoniqarsiz'),
    3: t('rating_3', '3 - O‘rtacha'),
    4: t('rating_4', '4 - Yaxshi'),
    5: t('rating_5', '5 - A’lo darajada (Tavsiya qilaman)'),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedLawyerId) {
      showToast(currentLang === 'uz' ? 'Iltimos, baholanadigan advokatni tanlang' : currentLang === 'ru' ? 'Пожалуйста, выберите адвоката' : 'Please select an attorney', 'error');
      return;
    }
    if (!clientName.trim() || clientName.trim().length < 2) {
      showToast(currentLang === 'uz' ? 'Ismingizni to‘liq kiriting' : currentLang === 'ru' ? 'Введите ваше полное имя' : 'Please enter your full name', 'error');
      return;
    }
    if (!comment.trim() || comment.trim().length < 10) {
      showToast(currentLang === 'uz' ? 'Izoh va mulohaza yozish majburiy (kamida 10 ta belgi)' : currentLang === 'ru' ? 'Отзыв обязателен (минимум 10 символов)' : 'Review comment is required (minimum 10 chars)', 'error');
      return;
    }

    const lawyer = lawyerList.find((l) => String(l.id) === String(selectedLawyerId));
    const lawyerLabel = lawyer ? (currentLang === 'uz' ? `Advokat: ${lawyer.fullName}` : currentLang === 'ru' ? `Адвокат: ${lawyer.fullName}` : `Attorney: ${lawyer.fullName}`) : '';
    const formattedTitle = clientTitle.trim() 
      ? `${clientTitle.trim()} (${lawyerLabel})`
      : lawyerLabel;

    setSubmitting(true);
    try {
      const res = await api.post('/reviews', {
        clientName: clientName.trim(),
        clientTitle: formattedTitle,
        rating: Number(rating),
        lawyerId: selectedLawyerId ? parseInt(selectedLawyerId, 10) : null,
        commentUz: comment.trim(),
        commentRu: comment.trim(),
        commentEn: comment.trim(),
        status: 'APPROVED',
        isFeatured: true,
      });

      if (res?.success) {
        setSubmitted(true);
        showToast(currentLang === 'uz' ? 'Fikringiz va bahoingiz muvaffaqiyatli qabul qilindi! Katta rahmat.' : currentLang === 'ru' ? 'Ваш отзыв успешно принят! Большое спасибо.' : 'Review received successfully! Thank you.', 'success');
        setTimeout(() => {
          setSubmitted(false);
          setClientName('');
          setClientTitle('');
          setComment('');
          setSelectedLawyerId('');
          setRating(5);
          onClose();
        }, 1500);
      } else {
        showToast(res?.message || 'Fikr yuborishda xatolik yuz berdi', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Xatolik yuz berdi', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      {/* Dark Backdrop */}
      <div 
        className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal Dialog */}
      <div className="relative w-full max-w-lg bg-[#070c16] border border-[#c59b27]/50 rounded-2xl shadow-2xl shadow-black/95 overflow-hidden z-10 my-auto max-h-[92vh] flex flex-col animate-in zoom-in-95 duration-200">
        
        {/* Top Gold Bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-[#b38038] via-[#c59b27] to-[#d4af37] shrink-0" />

        {/* Prominent Close Button (X) */}
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          aria-label={t('close_btn', 'Yopish')}
          className="absolute top-3.5 right-3.5 z-30 w-8 h-8 rounded-full bg-[#131d2e] hover:bg-[#1c2c46] border border-[#c59b27]/40 hover:border-[#c59b27] flex items-center justify-center text-slate-300 hover:text-white transition-all cursor-pointer disabled:opacity-50 shadow-md"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-5 sm:p-6 overflow-y-auto">
          {submitted ? (
            <div className="py-8 text-center space-y-3">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center text-emerald-400 mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="font-serif text-xl font-bold text-slate-100">
                {t('review_submitted_title', 'Bahoingiz Muvaffaqiyatli Qabul Qilindi!')}
              </h3>
              <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
                {t('review_submitted_subtitle', 'Advokatimiz faoliyatiga bildirgan xolis fikringiz va bahoingiz uchun minnatdorchilik bildiramiz.')}
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="text-center mb-5">
                <div className="w-11 h-11 rounded-xl bg-[#c59b27]/15 border border-[#c59b27]/60 flex items-center justify-center text-[#c59b27] mx-auto mb-2.5 shadow-lg shadow-black/60">
                  <Star className="w-5 h-5 fill-[#c59b27] text-[#c59b27]" />
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-100">
                  {t('rate_lawyer_title', 'Advokatga Baho Berish')}
                </h3>
                <p className="text-[11px] sm:text-xs text-slate-400 mt-1 font-light max-w-sm mx-auto">
                  {t('rate_lawyer_subtitle', 'Advokat xizmati sifati bo‘yicha 1 dan 5 gacha baho bering va xolis izohingizni qoldiring')}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                
                {/* 1. Lawyer Selection */}
                <div>
                  <label className="block text-xs uppercase font-semibold text-slate-300 mb-1.5 flex items-center">
                    <User className="w-3.5 h-3.5 text-[#c59b27] mr-1.5" />
                    {t('select_lawyer_label', 'Advokatni tanlang *')}
                  </label>
                  <select
                    required
                    value={selectedLawyerId}
                    onChange={(e) => setSelectedLawyerId(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-[#0b1220] border border-[#1b2b46] rounded-lg text-xs text-slate-100 focus:outline-none focus:border-[#c59b27] transition-colors"
                  >
                    <option value="">{t('select_lawyer_placeholder', '— Advokatni tanlang —')}</option>
                    {lawyerList.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.fullName} — {localizeLawyerPosition(l.position || l.title || 'Advokat', currentLang)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 2. Star Rating (1 to 5) */}
                <div className="bg-[#0b1220] p-3.5 rounded-xl border border-[#1b2b46] text-center space-y-2">
                  <label className="block text-xs uppercase font-semibold text-slate-300">
                    {t('rating_stars_label', 'Baho (1 dan 5 gacha yulduz) *')}
                  </label>
                  <div className="flex items-center justify-center space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const isFilled = (hoverRating || rating) >= star;
                      return (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          className="p-1 transition-transform hover:scale-125 cursor-pointer"
                        >
                          <Star 
                            className={`w-7 h-7 transition-colors ${
                              isFilled 
                                ? 'text-[#c59b27] fill-[#c59b27] drop-shadow-[0_0_8px_rgba(197,155,39,0.5)]' 
                                : 'text-slate-600'
                            }`} 
                          />
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs font-semibold text-[#c59b27]">
                    {ratingLabels[hoverRating || rating]}
                  </p>
                </div>

                {/* 3. Client Name */}
                <div>
                  <label className="block text-xs uppercase font-semibold text-slate-300 mb-1 flex items-center">
                    <User className="w-3.5 h-3.5 text-[#c59b27] mr-1.5" />
                    {t('client_name_label', 'Ismingiz va Familiyangiz *')}
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={t('client_name_placeholder', 'Masalan: Sardor Rahimov')}
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-[#0b1220] border border-[#1b2b46] rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#c59b27]"
                  />
                </div>

                {/* 4. Client Title (Optional) */}
                <div>
                  <label className="block text-xs uppercase font-semibold text-slate-300 mb-1 flex items-center">
                    <Briefcase className="w-3.5 h-3.5 text-[#c59b27] mr-1.5" />
                    {t('client_title_label', 'Kasbingiz yoki Tashkilotingiz (Ixtiyoriy)')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('client_title_placeholder', 'Masalan: «Delta Agro» MCHJ rahbari')}
                    value={clientTitle}
                    onChange={(e) => setClientTitle(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-[#0b1220] border border-[#1b2b46] rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#c59b27]"
                  />
                </div>

                {/* 5. Comment / Review (MANDATORY) */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs uppercase font-semibold text-slate-300 flex items-center">
                      <MessageSquarePlus className="w-3.5 h-3.5 text-[#c59b27] mr-1.5" />
                      {t('comment_label', 'Izoh va Mulohazangiz (Majburiy) *')}
                    </label>
                    <span className="text-[10px] text-amber-400 font-semibold uppercase">{currentLang === 'uz' ? 'Majburiy' : currentLang === 'ru' ? 'Обязательно' : 'Required'}</span>
                  </div>
                  <textarea
                    required
                    rows={3}
                    minLength={10}
                    placeholder={t('comment_placeholder', 'Advokatning ishi, maslahati va natijasi haqida batafsil yozing (kamida 10 ta belgi)...')}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-[#0b1220] border border-[#1b2b46] rounded-lg text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#c59b27] leading-relaxed resize-none"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">{currentLang === 'uz' ? 'Kamida 10 ta belgi kiritilishi lozim.' : currentLang === 'ru' ? 'Минимум 10 символов.' : 'Minimum 10 characters required.'}</p>
                </div>

                {/* Submit and Cancel Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="w-full py-2.5 px-4 rounded-lg border border-slate-700 hover:border-slate-500 text-xs font-semibold text-slate-300 hover:text-white transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {t('cancel_btn', 'Bekor qilish')}
                  </button>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-2.5 px-4 rounded-lg bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:from-[#c59b27] hover:to-[#d4af37] text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-[#b38038]/30 transition-all flex items-center justify-center cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                        {t('submitting', 'Yuborilmoqda...')}
                      </>
                    ) : (
                      t('submit_review_btn', 'Bahoni va Fikrni Yuborish')
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
